import React, { Fragment } from 'react'
import map from 'lodash/map'
import { Pagination } from '@mui/material'
import EmptyTablePlaceholder from '../../shared/EmptyTablePlaceholder'
import OrderGroup from './OrderGroup'
import TimeLegend from './TimeLegend'
import { externalIdLabel } from '../orderProviders'
import { columnKeys, isTimeColumn, OrdersTab, TIME_KINDS } from './columns'
import { t } from '../../../i18n'
import { OrderGroup as OrderGroupType, OrderProvider } from '../types'

const scope = 'orders'

const groupKey = (orderGroup: OrderGroupType): string => {
  if (orderGroup.order_id) return `order-${orderGroup.order_id}`

  const firstRow = orderGroup.queued_orders[0]
  if (!firstRow) return `order-${orderGroup.created_at_epoch}`

  return `${firstRow.record_type}-${firstRow.id}`
}

const OrdersTable = ({
  tab,
  orderGroups,
  orderProviders,
  currentPage,
  pageCount,
  onPageChange
}: {
  tab: OrdersTab
  orderGroups: OrderGroupType[]
  orderProviders: OrderProvider[]
  currentPage: string
  pageCount: number | null
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void
}) => {
  if (!orderGroups.length) {
    return tab === 'order_queue' ? (
      <EmptyTablePlaceholder
        heading={t('order.no_active_records_heading')}
        subtext={t('order.no_active_records_subtext')}
      />
    ) : (
      <EmptyTablePlaceholder heading={t('order.no_history_records_heading')} />
    )
  }

  const providerLabel = externalIdLabel(orderProviders)
  const tabModifier =
    tab === 'order_queue' ? 'orders-table--queue' : 'orders-table--history'

  return (
    <>
      <table className={`table table-robis orders-table ${tabModifier}`}>
        <thead className="thead-robis">
          <tr>
            {columnKeys(tab).map((key) => (
              <th key={key}>
                {t(key, { scope })}
                {isTimeColumn(key) && <TimeLegend kinds={TIME_KINDS[tab]} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {map(orderGroups, (orderGroup: OrderGroupType) => (
            <Fragment key={groupKey(orderGroup)}>
              <OrderGroup
                tab={tab}
                orderGroup={orderGroup}
                externalIdLabel={providerLabel}
              />
            </Fragment>
          ))}
        </tbody>
      </table>
      {tab === 'order_history' && pageCount !== null && pageCount > 1 && (
        <Pagination
          count={pageCount}
          size="large"
          page={Math.max(parseInt(currentPage, 10) || 1, 1)}
          variant="outlined"
          shape="rounded"
          onChange={onPageChange}
        />
      )}
    </>
  )
}

export default OrdersTable
